import { Link } from "@/lib/page-rendering"
import {
  ArrowLeft,
  BookOpen,
  Globe,
  Inbox,
  PlusCircle,
  Settings2,
  User,
  Users,
  X,
} from "lucide-react"
import { useEffect, useEffectEvent, useId, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  activeFilters,
  type ClassMember,
  type ClassSelection,
  FacetControls,
  type ReadyMembersState,
  ShortlistMembersProvider,
  type ShortlistSummary,
  useShortlistMembers,
  useShortlists,
} from "@search-shortlists"

import { ListingTypeBadge } from "@/components/shared/listing-type-badge"
import { AddPreferenceTabs } from "@/components/learners/preferences/tabs"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"

const TEXT_FILTER_DEBOUNCE_MS = 300

export type ShortlistSheetProps = {
  /** Receives the chosen class's selection token and the chosen educator's id, null for the listing profile. */
  onSelect: (selection: ClassSelection, teacherId: string | null) => void
  trigger: React.ReactElement
}

/**
 * The add-class sheet on a preference page, composed from the shortlist
 * module search exports. Learning owns the chrome, the tabs, which shortlist
 * is open, the facet popover, and the add action; search owns the data,
 * served as a state the sheet switches on and commands the sheet issues.
 */
export function ShortlistSheet({ onSelect, trigger }: ShortlistSheetProps) {
  const { t } = useTranslation("preferences")
  const [open, setOpen] = useState(false)
  const [shortlist, setShortlist] = useState<ShortlistSummary | null>(null)

  const changeOpen = (next: boolean) => {
    setOpen(next)
    if (!next) setShortlist(null)
  }

  return (
    <Sheet open={open} onOpenChange={changeOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        className="flex flex-col gap-4 overflow-y-auto"
        aria-describedby={undefined}
        data-testid="shortlist-sheet"
      >
        <SheetTitle className="sr-only">
          {t("shortlist_sheet.title")}
        </SheetTitle>
        <AddPreferenceTabs
          active={shortlist === null ? "shortlists" : "saved_classes"}
          onShowShortlists={() => setShortlist(null)}
        />
        {shortlist === null ? (
          <ShortlistPicker onChoose={setShortlist} />
        ) : (
          <ShortlistClasses
            shortlist={shortlist}
            onBack={() => setShortlist(null)}
            onSelect={(selection, teacherId) => {
              onSelect(selection, teacherId)
              changeOpen(false)
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}

function ShortlistPicker({
  onChoose,
}: {
  onChoose: (shortlist: ShortlistSummary) => void
}) {
  const { t } = useTranslation("preferences")
  const state = useShortlists()

  if (state.status === "loading") {
    return (
      <p className="text-muted-foreground text-sm">
        {t("shortlist_sheet.loading")}
      </p>
    )
  }
  if (state.status === "error") {
    return <p role="alert">{t("shortlist_sheet.error")}</p>
  }
  if (state.shortlists.length === 0) {
    return (
      <Empty data-testid="shortlist-sheet-empty">
        <EmptyHeader>
          <EmptyMedia variant="icon" className="text-muted size-20 bg-white">
            <Inbox className="size-16" />
          </EmptyMedia>
          <EmptyTitle>{t("shortlist_sheet.empty_title")}</EmptyTitle>
          <EmptyDescription>
            {t("shortlist_sheet.empty_description")}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button asChild>
            <Link href="/search/shortlists/new">
              <PlusCircle />
              {t("shortlist_sheet.create_shortlist")}
            </Link>
          </Button>
        </EmptyContent>
      </Empty>
    )
  }

  return (
    <ul
      className="flex flex-col gap-4"
      aria-label={t("shortlist_sheet.shortlists_label")}
      data-testid="shortlist-sheet-list"
    >
      {state.shortlists.map((shortlist) => (
        <ShortlistRow
          key={shortlist.id}
          shortlist={shortlist}
          onChoose={onChoose}
        />
      ))}
    </ul>
  )
}

function ShortlistRow({
  shortlist,
  onChoose,
}: {
  shortlist: ShortlistSummary
  onChoose: (shortlist: ShortlistSummary) => void
}) {
  const { t } = useTranslation("preferences")
  const labelId = useId()

  return (
    <li aria-labelledby={labelId} data-testid="shortlist-sheet-shortlist">
      <Card>
        <CardHeader>
          <CardTitle id={labelId}>{shortlist.name}</CardTitle>
          <p className="text-muted-foreground text-sm">
            {t("shortlist_sheet.class_count", { count: shortlist.classCount })}
            {" · "}
            {t("shortlist_sheet.educator_count", {
              count: shortlist.educatorCount,
            })}
          </p>
        </CardHeader>
        <CardFooter>
          <Button onClick={() => onChoose(shortlist)}>
            <BookOpen />
            {t("shortlist_sheet.view_classes")}
          </Button>
        </CardFooter>
      </Card>
    </li>
  )
}

function ShortlistClasses({
  shortlist,
  onBack,
  onSelect,
}: {
  shortlist: ShortlistSummary
  onBack: () => void
  onSelect: ShortlistSheetProps["onSelect"]
}) {
  const { t } = useTranslation("preferences")
  const [includeEducatorClasses, setIncludeEducatorClasses] = useState(false)
  const includeId = useId()

  return (
    <ShortlistMembersProvider
      shortlistId={shortlist.id}
      kind="class"
      includeEducatorClasses={includeEducatorClasses}
    >
      <div className="bg-background sticky top-0 z-10 flex flex-row items-center gap-4 pb-2">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft />
          {t("shortlist_sheet.back")}
        </Button>
        <h2 className="inline-flex items-center gap-1 text-2xl font-semibold tracking-tight">
          <BookOpen size={24} />
          {shortlist.name}
        </h2>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={includeId}
          checked={includeEducatorClasses}
          onCheckedChange={(checked) =>
            setIncludeEducatorClasses(checked === true)
          }
        />
        <Label htmlFor={includeId}>
          {t("shortlist_sheet.include_educator_classes")}
        </Label>
      </div>
      <ClassFilters />
      <ClassMembers onSelect={onSelect} />
    </ShortlistMembersProvider>
  )
}

function ClassFilters() {
  const { t } = useTranslation("preferences")
  const { state, commands } = useShortlistMembers()
  const appliedText = state.status === "ready" ? state.text : ""
  const [query, setQuery] = useState(appliedText)
  const applyQuery = useEffectEvent((value: string) => {
    if (value !== appliedText) commands.setText(value)
  })

  useEffect(() => {
    const timer = setTimeout(() => applyQuery(query), TEXT_FILTER_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const facets = state.status === "ready" ? state.facets : []
  const filters = state.status === "ready" ? activeFilters(state) : []

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2">
        <Input
          type="search"
          aria-label={t("shortlist_sheet.filter_label")}
          placeholder={t("shortlist_sheet.filter_placeholder")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Popover modal>
          <PopoverTrigger asChild>
            <Button variant="outline">
              <Settings2 />
              {t("shortlist_sheet.filters")}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            collisionPadding={{ right: 20 }}
            className="max-h-120 w-80 overflow-y-auto overscroll-contain"
            data-testid="shortlist-sheet-facets"
          >
            <FacetControls facets={facets} onToggle={commands.toggleFacet} />
          </PopoverContent>
        </Popover>
      </div>
      {filters.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="shortlist-sheet-active-filters"
        >
          {filters.map((filter) =>
            filter.kind === "facet" ? (
              <ActiveFilterPill
                key={`${filter.facetId}:${filter.value}`}
                label={filter.label}
                onRemove={() =>
                  commands.toggleFacet(filter.facetId, filter.value)
                }
              />
            ) : (
              <ActiveFilterPill
                key="text"
                label={`"${filter.text}"`}
                onRemove={() => {
                  setQuery("")
                  commands.setText("")
                }}
              />
            ),
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => {
              setQuery("")
              commands.clearAll()
            }}
          >
            {t("shortlist_sheet.clear_filters")}
          </Button>
        </div>
      )}
    </div>
  )
}

function ActiveFilterPill({
  label,
  onRemove,
}: {
  label: string
  onRemove: () => void
}) {
  return (
    <Badge
      variant="active"
      className="cursor-pointer gap-1 pr-1"
      data-testid="shortlist-sheet-active-filter"
      onClick={onRemove}
    >
      <span>{label}</span>
      <X className="size-3" />
    </Badge>
  )
}

function ClassMembers({
  onSelect,
}: {
  onSelect: ShortlistSheetProps["onSelect"]
}) {
  const { t } = useTranslation("preferences")
  const { state, commands } = useShortlistMembers()

  if (state.status === "error") {
    return <p role="alert">{t("shortlist_sheet.classes_error")}</p>
  }
  if (state.status === "loading") {
    return (
      <p className="text-muted-foreground text-sm">
        {t("shortlist_sheet.classes_loading")}
      </p>
    )
  }
  if (state.members.length === 0) {
    return (
      <Empty data-testid="shortlist-sheet-no-classes">
        <EmptyHeader>
          <EmptyTitle>{t("shortlist_sheet.classes_empty_title")}</EmptyTitle>
          <EmptyDescription>
            {t("shortlist_sheet.classes_empty_description")}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <>
      <ul
        className={cn("flex flex-col gap-4", state.refreshing && "opacity-60")}
        aria-busy={state.refreshing}
        data-testid="shortlist-sheet-classes"
      >
        {state.members.map((member) =>
          member.kind === "class" ? (
            <ClassCard key={member.key} member={member} onSelect={onSelect} />
          ) : null,
        )}
      </ul>
      <ClassPagination state={state} setPage={commands.setPage} />
    </>
  )
}

function ClassPagination({
  state,
  setPage,
}: {
  state: ReadyMembersState
  setPage: (page: number) => void
}) {
  const { t } = useTranslation("preferences")
  const { number, count: pageCount } = state.page

  if (pageCount <= 1) return null

  return (
    <nav
      className="flex items-center justify-between"
      aria-label={t("shortlist_sheet.pagination")}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={number <= 1}
        onClick={() => setPage(number - 1)}
      >
        {t("shortlist_sheet.previous_page")}
      </Button>
      <span className="text-sm">
        {t("shortlist_sheet.paging", { page: number, pageCount })}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={number >= pageCount}
        onClick={() => setPage(number + 1)}
      >
        {t("shortlist_sheet.next_page")}
      </Button>
    </nav>
  )
}

function ClassCard({
  member,
  onSelect,
}: {
  member: ClassMember
  onSelect: ShortlistSheetProps["onSelect"]
}) {
  const { t } = useTranslation("preferences")
  const labelId = useId()

  return (
    <li aria-labelledby={labelId} data-testid="shortlist-sheet-class">
      <Card>
        <CardHeader className="p-3">
          <CardTitle className="text-xl" id={labelId}>
            {member.name}
          </CardTitle>
          <CardDescription>
            {member.subjectLabel} ({member.curriculumLabel})
          </CardDescription>
        </CardHeader>
        <Separator />
        <CardContent className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
          <div>
            <p className="text-muted-foreground inline-flex items-center gap-1 text-sm">
              <Users className="h-4 w-4" />
              {t("shortlist_sheet.class_size")}
            </p>
            <p className="text-foreground text-base">
              {t("shortlist_sheet.class_size_range", {
                min: member.classSize.min,
                max: member.classSize.max,
              })}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground inline-flex items-center gap-1 text-sm">
              <Globe className="h-4 w-4" />
              {t("shortlist_sheet.timezone")}
            </p>
            <p className="text-foreground text-base wrap-break-word">
              {member.timezone}
            </p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-muted-foreground inline-flex items-center gap-1 text-sm">
              <User className="h-4 w-4" /> {t("shortlist_sheet.educator")}
            </p>
            <div className="flex flex-row items-center gap-2">
              <p className="text-foreground space-x-2 text-base">
                {member.listingProfile.name}
              </p>
              <ListingTypeBadge listingType={member.listingProfile.type} />
            </div>
          </div>
        </CardContent>
        <Separator />
        <CardFooter className="flex flex-col items-start p-3">
          <h3 className="text-muted-foreground text-sm">
            {t("shortlist_sheet.choose_educator")}
          </h3>
          <ul className="w-full space-y-2">
            {member.educators.map((educator) => (
              <li
                key={educator.id ?? "listing-profile"}
                className="flex w-full flex-row items-center justify-between"
              >
                <span>{educator.name}</span>
                <Button onClick={() => onSelect(member.selection, educator.id)}>
                  {t("new.add")}
                </Button>
              </li>
            ))}
          </ul>
        </CardFooter>
      </Card>
    </li>
  )
}
